import api from "@/services/api.service";
import ccureapi from "@/services/api.ccure.service";
import router from "@/router";
import { ActionTree } from "vuex";
import {
	AlarmQueueFilter,
	AlarmQueueFilterEditModel,
	AlarmQueueFilterID,
	CallWaitingPref,
	EventsQueueState,
	FilteredEvent,
	HighPriorityIgnoreRequest,
	LoadServerEventTypesParams,
	SearchServerEventTypesParams,
	SiteTreeNode,
	SystemEventRecord
} from "./types";
import { RootState, UserGroup } from "../types";
import { get } from "lodash";
import { setDisplayTitle } from "./helpers";
import EventTypes from "@/types/EventTypes";

export const actions: ActionTree<EventsQueueState, RootState> = {
	async fetchEventQueue({ dispatch, commit, state, getters, rootGetters }, alarmFilters?: AlarmQueueFilterID[]) {
		if (!alarmFilters) {
			alarmFilters = await getters.getAlarmQueueFilterIDs;
		}
		try {
			const currentSort = state.CurrentSort;
			let filteredEventsResponse: any = null;

			// create payload to post to url defined above
			const requestData = {
				filters: alarmFilters,
				sortBy: currentSort.sort,
				sortDesc: currentSort.desc,
				includeAll: true
			};

			let eventQueueRequestUrl = state.eventQueueGetOverride ?? "/eventqueue";
			filteredEventsResponse = await api.eventQueueRequest(requestData, eventQueueRequestUrl);
			// Get calls out of filtered Event Response
			if (filteredEventsResponse.status == 200 && filteredEventsResponse.data.hasOwnProperty("callQueue")) {
				commit("setCallQueue", filteredEventsResponse.data.callQueue ?? []);
			}

			if (filteredEventsResponse.status == 200 && filteredEventsResponse.data.hasOwnProperty("callClients")) {
				commit("setCallClients", filteredEventsResponse.data.callClients ?? []);
			}

			const alarmQueueFilters = state.AlarmQueueFilters;
			const filterObj: any = {};

			alarmQueueFilters.forEach((filter: any) => {
				let name = filter.alarmQueueFilterID.toString();

				if (filter.regionID) {
					name = filter.alarmQueueFilterID.toString() + "_" + filter.regionID.toString();
				}

				filterObj[name] = {
					activeAlarmCount: 0
				};
			});

			const currentFilter = state.AlarmActiveFilter;
			const { eventQueue: eventsList } = filteredEventsResponse.data;
			const { systemQueue: systemEventsList } = filteredEventsResponse.data;

			let features = await rootGetters.getFeaturesList;
			let serverEventTitleInQueueEnabled: boolean = get(features, [
				"Alarms",
				"EventQueue",
				"ServerEventTitleInQueue"
			]);

			const filteredEvents: any[] = [];
			let includeCurrentFilter: boolean;

			eventsList.forEach(eventQueue => {
				includeCurrentFilter =
					currentFilter.filterId === -1
						? true
						: currentFilter.filterId === 0
						? eventQueue.inFilters != null
						: eventQueue.inFilters != null &&
						eventQueue.inFilters.some(el => {
								return (
									// We do an extra check here as region ID of 0 is 'local' for
									// event queue items.
									(el.regionID === 0 && currentFilter.regionId === null) ||
									(el.regionID === currentFilter.regionId && el.filterID === currentFilter.filterId)
								);
						});

				if (includeCurrentFilter) {
					eventQueue.displayTitle = setDisplayTitle(eventQueue, !serverEventTitleInQueueEnabled);

					filteredEvents.push(eventQueue);
				}

				if (eventQueue.eventTypeID === 2) {
					eventQueue.eventTitle = "Tour";
				}

				if (
					!eventQueue.inProcessing &&
					eventQueue.eventTypeID === 1 &&
					eventQueue.unParkAt == null &&
					eventQueue.isPermanentParked == false
				) {
					if (filterObj["-1"] != null) {
						filterObj["-1"].activeAlarmCount++;
					}

					if (eventQueue.inFilters != null) {
						filterObj["0"].activeAlarmCount++;

						eventQueue.inFilters.forEach((filter: any) => {
							let name = filter.filterID.toString();

							if (filter.regionID) {
								name = filter.filterID.toString() + "_" + filter.regionID.toString();
							}

							if (filterObj[name]) {
								filterObj[name].activeAlarmCount++;
							}
						});
					}
				}

				if (eventQueue.regionID === 0 && eventQueue.regionTitle === null) {
					eventQueue.regionTitle = "Local";
				}
			});

			commit("setAllEvents", eventsList);

			systemEventsList.forEach(eventQueue => {
				filteredEvents.push(eventQueue);
			});

			commit("setDataError", false, { root: true });

			// @techdebt all this conditions should be removed after migrating to websockets
			alarmQueueFilters.forEach((filter: any) => {
				let name = filter.alarmQueueFilterID.toString();

				if (filter.regionID) {
					name = filter.alarmQueueFilterID.toString() + "_" + filter.regionID.toString();
				}

				const alarmCount = filterObj[name].activeAlarmCount;

				if (filter.activeAlarmCount !== alarmCount) {
					commit("setAlarmQueueFilterAlarmCount", {
						filter,
						alarmCount
					});
				}
			});

			commit("setFilteredEvents", filteredEvents);
		} catch (err) {
			console.log(err);

			if (err.response != null && err.response.status === 401) {
				dispatch("logout");
			} else {
				commit("setDataError", true, { root: true });
			}
		}
	},

	addFilteredEvents({ getters, commit }, data: any) {
		data.forEach(event => {
			const storedEvent = getters.getFilteredEventById(event.eventID);

			commit("addFilteredEvents", { storedEvent, event });
		});
	},

	async loadAlarmQueueFiltersForAll({ commit }) {
		const alarmFilters = await api.loadAlarmQueueFilters();
		commit("setAlarmQueueFilters", alarmFilters || []);
	},

	async fetchAlarmQueueFilters({ dispatch, commit, state }) {
		const response = await api.loadAlarmQueueFilters();

		const filters: AlarmQueueFilter[] = response.data;
		const filtersIds: AlarmQueueFilterID[] = [];
		const activeFilter = state.AlarmActiveFilter;

		const allItem: AlarmQueueFilter = {
			alarmQueueFilterID: -1,
			title: "All",
			isActive: activeFilter.filterId === -1,
			activeAlarmCount: 0,
			hide: false,
			regionID: 0,
			regionalFilterID: "0_-1", // <RegionalID>_<FilterID>,
			isLocal: true
		};

		const allFiltersItem: AlarmQueueFilter = {
			alarmQueueFilterID: 0,
			title: "All Open Filters",
			isActive: activeFilter.filterId === 0 && filters.length > 0,
			activeAlarmCount: 0,
			hide: filters.length === 0,
			regionID: 0,
			regionalFilterID: "0_0",
			isLocal: true
		};

		filters.forEach(filter => {
			filter.isActive =
				activeFilter.filterId === filter.alarmQueueFilterID && activeFilter.regionId === filter.regionID;
			filter.activeAlarmCount = 0;
			filtersIds.push({
				filterId: filter.alarmQueueFilterID,
				regionId: filter.regionID
			});

			filter.isLocal = (filter.regionID || 0) === 0;

			if (!filter.addedRegionTitle && !filter.isLocal) {
				filter.title = `${filter.title} (${filter.regionTitle})`;
			}
		});

		let anyActive = allFiltersItem.isActive || allItem.isActive;
		if (!anyActive) {
			filters.forEach(filter => {
				if (filter.isActive) {
					anyActive = true;
				}
			});
		}

		if (!anyActive) {
			allItem.isActive = true;
			dispatch("setActiveFilter", { filterId: -1, regionId: null });
		}

		filters.unshift(allFiltersItem);
		filters.unshift(allItem);

		commit("setAlarmQueueFilters", filters);
		commit("setAlarmQueueFilterIDs", filtersIds);

		await dispatch("updateFilteredEvents", filtersIds);
		return filtersIds;
	},

	updateFilteredEvents({ dispatch, commit, state }, data: any) {
		const activeId = data || [{ filterId: 0, regionId: null }];
		dispatch("fetchEventQueue", activeId);
	},

	async handleEvent(
		{ dispatch, commit, state, getters, rootGetters },
		{ eventID, checkInProcessing, regionId, regionUrl }: any
	) {
		if (getters.getHandlingEvent) {
			return;
		}

		// Default region id/url to local if it wasn't provided
		regionId = regionId || 0;
		regionUrl = regionUrl || api.getOriginalApiUrl;

		commit("setHandlingEvent", true);
		commit("setJoiningEventID", eventID);

		try {
			const { data } = await api.loadEventHandleAllowInProcessing(eventID, checkInProcessing, regionUrl);
			const { canProcess, removedFromEvent, inProcessing, eventTypeId } = data;

			if (eventTypeId == EventTypes.Call && canProcess) {
				// Only set 'lastEventHandled' property on call event types because we do not want to re-route the user
				commit("setLastEventHandled");
			} else if (canProcess) {
				commit("setLastEventHandled");

				// check if HandleInNewTab feature is enabled
				let features = await rootGetters.getFeaturesList;
				let openInNewTabFeatureEnabled = get(features, ["Alarms", "HandleInNewTab"]);
				let userInViews = router.currentRoute && router.currentRoute.name == "views";

				if (userInViews) {
					// Set the previous route so the user will be returned to views when they finish the event
					commit("setPreviousRoute", "/views", { root: true });
				}

				const route = {
					path: `/site-monitor/${eventID}`,
					query: regionId > 0 ? { regionId } : null
				};

				// If the user is coming from views, and has open in new tab enabled, we'll open a new window.
				if (userInViews && openInNewTabFeatureEnabled) {
					// Get the route data for the site monitor
					const routeData = router.resolve(route);

					// open the site monitor in a new window
					window.open(routeData.href, "site-monitor");
				} else {
					router.push(route).catch(err => {});
				}
			} else {
				if (removedFromEvent) {
					commit("setProcessingError", "Unable to process event - you have been removed from this event.");
				} else if (!checkInProcessing && inProcessing) {
					commit("setProcessingError", "Unable to process event - event is already being processed.");
				} else {
					commit("setProcessingError", "Unable to process event");
				}

				// throw an error here so the caller knows to reset our region and base URL
				throw new Error("Unable to process event");
			}
		} finally {
			commit("setHandlingEvent", false);
		}
	},

	/**
	 * @deprecated This method is deprecated as it can cause performance issues on large systems, use the Groups API
	 * instead.
	 */
	async sitesTreeGet({ commit }, multiTenant?: boolean) {
		console.warn("Deprecated function used: 'sitesTreeGet' likely to cause performance issues. Use Groups API instead.");
		const data = await api.loadGroupsTree(multiTenant);
		commit("setTree", data);
	},

	/**
	 * @deprecated This method is deprecated as it can cause performance issues on large systems, use the Groups API
	 * instead.
	 */
	async sitesTreeGetWithRegion({ commit }, { multiTenant, regionURL }) {
		const data = await api.loadGroupsTreeWithRegion(multiTenant, regionURL);
		commit("setTree", data);
	},

	findGroupInTreeById({ getters }, groupId: number) {
		const recursiveFind = (tree: SiteTreeNode, groupId: number) => {
			if (tree.group.groupID === groupId) {
				return tree.group;
			}

			if (tree.subGroups && tree.subGroups.length > 0) {
				for (var i = 0; i < tree.subGroups.length; i++) {
					const searchResult = recursiveFind(tree.subGroups[i], groupId);
					if (searchResult) return searchResult;
				}
			}

			return null;
		};

		for (var i = 0; i < getters.getSiteTree.length; i++) {
			const searchResult = recursiveFind(getters.getSiteTree[i], groupId);
			if (searchResult) return searchResult;
		}
		return null;
	},

	async getSiteTreeRootGroup({ getters }) {
		await getters.getSiteTree.forEach(treeGroup => {
			if (treeGroup.group !== null && treeGroup.group.parentGroupID == null) {
				return treeGroup.group;
			}
		});
	},

	async loadAvailableAlarmQueueFilters() {
		const { data } = await api.loadAvailableAlarmQueueFilters();

		data.forEach(filter => {
			filter.isLocal = (filter.regionID || 0) === 0;
		});

		return data;
	},

	async addAlarmQueueFilter({ dispatch }, id: AlarmQueueFilterID) {
		await api.addAlarmQueueFilter(id);
		return await dispatch("fetchAlarmQueueFilters");
	},

	async removeAlarmQueueFilter({ dispatch }, id: AlarmQueueFilterID) {
		await api.removeAlarmQueueFilter(id);
		return await dispatch("fetchAlarmQueueFilters");
	},

	async eventHandleTop({ dispatch }, id: AlarmQueueFilterID) {
		return await api.eventHandleTop(id);
	},

	async createAlarmQueueFilter({ dispatch }, data: AlarmQueueFilterEditModel) {
		Object.keys(data).forEach(key => {
			if (key === "groups") {
				return;
			}

			if (data[key] === null) {
				delete data[key];
				return;
			}

			if (/Operator$/.test(key)) {
				data[key] = data[key] && data[key].toLowerCase();
			}
			if (/Value$/.test(key)) {
				// Convert strings to numbers in "*Value" props
				data[key] = data[key] && parseInt(data[key], 10);
			}
		});

		const alarmQueueFilterID = await api.createAlarmQueueFilter(data);
		await dispatch("fetchAlarmQueueFilters");

		return alarmQueueFilterID.data;
	},

	async editAlarmQueueFilter({ dispatch }, data: AlarmQueueFilterEditModel) {
		Object.keys(data).forEach(key => {
			if (data[key] === null) {
				delete data[key];
			}
		});

		await api.editAlarmQueueFilter(data);
		await dispatch("fetchAlarmQueueFilters");

		return data.alarmQueueFilterID;
	},

	async deleteAlarmQueueFilter({ dispatch }, filterId: number) {
		await api.deleteAlarmQueueFilter(filterId);
		await dispatch("fetchAlarmQueueFilters");
	},

	collapseSiteTree({ dispatch, commit }, { groups, lvl, collapsedLevel }) {
		const nextLvl = lvl + 1;
		groups.forEach(group => {
			if (lvl === collapsedLevel) {
				commit("collapseSiteTreeNode", group);
			}

			if (lvl < collapsedLevel && group.subGroups.length) {
				commit("expandSiteTreeNode", group);
				dispatch("collapseSiteTree", {
					groups: group.subGroups,
					nextLvl,
					collapsedLevel
				});
			}
		});
	},

	/**
	 * Expands the entire site tree.
	 */
	expandSiteTree({ commit, state }) {
		// Resets a tree node (and it's children) to be expanded
		function resetTreeNode(node: SiteTreeNode) {
			commit("expandSiteTreeNode", node);

			if (node.subGroups && node.subGroups.length > 0) {
				node.subGroups.forEach(resetTreeNode);
			}
		}

		state.SiteTree.forEach(group => {
			resetTreeNode(group);
		});
	},

	async loadRoutingGroups({ commit }) {
		const { data } = await api.loadRoutingGroups();
		commit("setRoutingGroups", data);
	},

	async loadAlarmTags({ commit }) {
		const { data } = await api.loadAlarmTags();
		commit("setAlarmTags", data);
	},

	async fetchUserZone({ commit }: any) {
		const userZone = await api.loadUserZone();
		commit("setUserZone", userZone);
	},

	setDisplayedEvent({ commit }, event) {
		commit("setDisplayedEvent", event);
	},

	async eventControl(context, payload) {
		const { data } = await api.eventControl(payload);
		return data;
	},

	async tourNotesRequired({ commit }) {
		// execute api tourNotesRequired Method.
		const { data } = await api.tourNotesRequired();
		// Commit the returned data.
		commit("setTourNotesRequired", data);
	},

	async loadServerEventTypes({ commit }, { serverType, groupID, pageSize, regionId }: LoadServerEventTypesParams) {
		// passing anything other than on object with 'serverType' as a property will cause this to be undefined
		if (serverType === undefined) {
			throw new Error("Action:loadServerEventTypes - serverType parameter is invalid");
		}

		const { data } = await api.loadServerEventTypes(serverType, groupID, pageSize, null, regionId);
		commit("setServerEventTypes", data);
	},

	async searchServerEventTypes({ commit }, { serverType, groupID, filter, pageSize }: SearchServerEventTypesParams) {
		const { data } = await api.loadServerEventTypes(serverType, groupID, pageSize, filter);
		commit("setFilteredServerEventTypes", data);
	},

	async searchGroups(context, params) {
		const { data } = await api.searchGroups(params);
		return data as UserGroup[];
	},

	async loadGroupsList({ commit }) {
		return await api.loadGroupsList();
	},

	async clearEventQueue(context, id: number) {
		const { data } = await api.clearEventQueue(id);
		return data;
	},

	setActiveFilter({ commit }, alarmQueueFilterID: AlarmQueueFilterID) {
		commit("setActiveFilter", alarmQueueFilterID);
	},

	async loadEventShareInvites({ commit, dispatch }) {
		try {
			const { data } = await api.loadEventShareInvites();

			return data;
		} catch (err) {
			console.log(err);

			if (err.response != null && err.response.status === 401) {
				dispatch("logout");
			} else {
				commit("setDataError", true, { root: true });
			}
		}
	},

	async acceptEventShareInvite(context, eventUserID: number) {
		const { data } = await api.acceptEventShareInvite(eventUserID);
		return data;
	},

	async ignoreEventShareInvite(context, eventUserID: number) {
		const { data } = await api.ignoreEventShareInvite(eventUserID);
		return data;
	},

	async fetchGroupsForFilter(context, filterID: number) {
		return await api.groupsForFilter(filterID);
	},

	async resetAllActions(context, eventId: number) {
		return await ccureapi.resetAllActions(eventId);
	},

	async resetAllActionsWithRegion(context, data: any) {
		return await ccureapi.resetAllActionsWithRegion(data.eventId, data.regionId);
	},

	setHideMapFlag({ commit }, hideMapFlag: boolean) {
		commit("setHideMapFlag", hideMapFlag);
	},

	async getSystemEventRecords({ commit }, eventIds: number[]) {
		const response = await api.loadSystemEventRecords(eventIds);
		const eventRecords: SystemEventRecord[] = response.data;
		commit("setSystemEventRecords", eventRecords);
	},

	async fetchHighPriThreshold({ commit }) {
		const { data } = await api.fetchThresholdHighPriority();
		commit("setHighPriorityThreshold", data);
	},

	// Gets call waiting preferences to highlight calls based on wait time (minutes)
	async getCallWaitingPrefs({ commit }) {
		const response = await api.GetCallWaitingPrefs();
		const callWaitingPrefs: CallWaitingPref[] = response?.data;
		commit("setCallWaitingPrefs", callWaitingPrefs);
	},

	/**
	 * Allows the user to ignore a high priority alarm.
	 * Starts the loading spinner in the banner and then sends an ignore request.
	 * @param commit For commiting mutations
	 * @param state The Event Queue state
	 * @param highPriAlarm The high priority event to ignore
	 */
	async ignoreHighPriEvent({ commit, state }, highPriAlarm: FilteredEvent) {
		if (state.HighPriorityState.LoadingIgnore || state.HighPriorityState.LoadingAccept) {
			return;
		}

		commit("setHighPriorityIgnoreLoading", true);

		const request: HighPriorityIgnoreRequest = {
			eventId: highPriAlarm.eventID,
			eventRegionId: highPriAlarm.regionID,
			eventRegionUrl: highPriAlarm.regionURL
		};

		const response = await api.ignoreHighPriority(request);

		if (response.status != 200) {
			commit("setHighPriorityIgnoreLoading", false);
			throw new Error(response.statusText);
		}

		commit("setEventIgnored", request.eventId);
		commit("setHighPriorityIgnoreLoading", false);
	}
};
